import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CameraIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { useProdutoPorBarras } from '../../hooks/useConferencias';
import { useToast } from '../ui/Toast';
import { indexarEmbalagensPorEan } from '../../utils/statusConferencia';
import { criarControleLeitura } from '../../utils/scanCooldown';
import { parseEntradaBipagem } from '../../utils/multiplicadorBipagem';
import { feedbackSucesso, feedbackErro, prepararAudio } from '../../utils/feedbackLeitura';
import type { ConferenciaNfeItem } from '../../types/conferencia';
import { inputIris } from '../ui/iris';
import CameraScanner from './CameraScanner';

interface ScanBarProps {
  /** Itens JÁ CARREGADOS da conferência — o match por EAN é 100% local (sem round-trip),
      é o que deixa a bipagem instantânea. */
  itens: ConferenciaNfeItem[];
  /** Desabilitada quando a conferência está `concluida` — não deixa reabrir contagem por bipe. */
  disabled?: boolean;
  /** Chamado quando o EAN casa com alguma embalagem desta nota. Quem persiste
      (useAtualizarItem) é o workspace, que também posiciona e destaca a linha bipada.
      `unidades` já é o multiplicador da embalagem lida; `unidade` é o rótulo pro aviso.
      Resolve com `true` se a leitura foi REGISTRADA, `false` se o operador cancelou (ex.:
      recusou o excedente). O modo câmera AGUARDA essa promise pra não empilhar diálogos E
      pra só consumir o multiplicador one-shot quando a leitura de fato contou. */
  onBipar: (item: ConferenciaNfeItem, unidades: number, unidade: string | null) => boolean | Promise<boolean>;
  /** Liga o atalho de multiplicação ("N*código" digitado + stepper na câmera). Vem de
      `conferencia.multiplicadorAtivo` (parâmetro global). Off = leitura crua (comportamento atual). */
  multiplicadorAtivo?: boolean;
  /** Câmera controlada pelo pai (o workspace mobile orquestra câmera + card de última leitura).
      Ausente = estado interno (desktop / standalone). */
  cameraAtiva?: boolean;
  onCameraChange?: (v: boolean) => void;
  /** Layout mobile: esconde o stepper de multiplicador (vai pro card) e usa câmera compacta. */
  isMobile?: boolean;
}

/**
 * Id do input de bipagem. Estável de propósito: é o contrato que qualquer overlay do
 * workspace (foto ampliada, galeria de fotos) usa pra devolver o foco à barra. A alternativa
 * — um ref/callback vindo da página — teria que atravessar `ItemConferenciaLinha` até a
 * `FotoProduto`, acoplando a linha do checklist a um detalhe da barra pra nada.
 */
export const SCAN_INPUT_ID = 'conferencia-scan-input';

// Ligado pela ScanBar enquanto o modo câmera está ativo. focarScanBar vira no-op nesse estado:
// no modo câmera o operador mira o preview, e focar o input numérico popparia o teclado do
// celular POR CIMA da câmera (o input existe só pro leitor USB / digitação, que ninguém usa
// enquanto lê pela câmera).
let cameraModeAtivo = false;

/**
 * Devolve o foco à barra de bipagem. Todo componente que ROUBA o foco durante a conferência
 * deve chamar isto ao terminar: o leitor keyboard-wedge digita onde estiver o foco e, se for
 * o `body`, o Enter não submete nada — o scan some sem toast, sem erro e sem contagem, que é
 * exatamente o que o módulo promete nunca fazer.
 *
 * <p>`setTimeout(0)` pelo mesmo motivo do refoco interno: deixa o React desmontar o overlay
 * antes de recolocar o cursor, senão o desmonte joga o foco no body DEPOIS do nosso focus().</p>
 */
export function focarScanBar() {
  if (cameraModeAtivo) return;
  setTimeout(() => document.getElementById(SCAN_INPUT_ID)?.focus(), 0);
}

/**
 * Barra de bipagem — protagonista do workspace de conferência. Duas formas de bipar, o mesmo
 * caminho de processamento (`processarScan`):
 * - **Leitor USB "keyboard wedge"**: digita no input e finaliza com Enter.
 * - **Câmera do aparelho** (botão ao lado): modo contínuo, o preview fica no topo e cada código
 *   decodificado vira um bipe. `criarControleLeitura` garante 1 scan físico = 1 bipe (a câmera
 *   dispara o mesmo código dezenas de vezes/s), e `bipandoRef` serializa — um bipe por vez, pra
 *   não empilhar o diálogo de excedente. Feedback tátil/sonoro (`feedbackLeitura`) porque na
 *   doca o operador mira o volume, não olha os números.
 *
 * <p>O código lido decide QUANTO soma: cada item traz todas as embalagens da sua família
 * (mesmo PRODUTOPRINCIPAL no ERP), então bipar a caixa soma a QTDEEMBALAGEM dela e bipar a
 * unidade soma 1.</p>
 *
 * <p>Nunca ignora um scan sem match: avisa por toast, enriquecido com `produtoPorBarras`
 * quando o código existe no cadastro (mesmo fora desta NF-e), pra o operador saber SE bipou
 * o produto errado ou um código que nem existe.</p>
 */
const ScanBar: React.FC<ScanBarProps> = ({
  itens, disabled = false, onBipar, multiplicadorAtivo = false,
  cameraAtiva: cameraAtivaProp, onCameraChange, isMobile = false,
}) => {
  const [codigo, setCodigo] = useState('');
  // Câmera controlada pelo pai quando `cameraAtiva`/`onCameraChange` vêm (workspace mobile);
  // senão estado interno (desktop / standalone). Os nomes `cameraAtiva`/`setCameraAtiva` seguem
  // valendo downstream — só a origem muda.
  const [camInterna, setCamInterna] = useState(false);
  const cameraAtiva = cameraAtivaProp ?? camInterna;
  const setCameraAtiva = (v: boolean) => (onCameraChange ?? setCamInterna)(v);
  // Multiplicador do modo câmera (desktop) — one-shot: aplica na próxima leitura e volta pra 1.
  // No mobile o stepper some (vai pro card do workspace) e este fica em 1.
  const [multCamera, setMultCamera] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const produtoPorBarras = useProdutoPorBarras();

  // Índice EAN → destino, remontado só quando a lista de itens muda (não a cada tecla).
  const porEan = useMemo(() => indexarEmbalagensPorEan(itens), [itens]);

  // Estado do modo câmera (refs, não state: mudam por leitura, não devem re-renderizar).
  const bipandoRef = useRef(false);             // serializa: 1 bipe por vez
  const controleLeitura = useRef(criarControleLeitura()); // 1 scan físico = 1 bipe

  // Conferência concluída no meio da leitura por câmera → fecha a câmera (para o stream).
  useEffect(() => {
    if (disabled) setCameraAtiva(false);
    // setCameraAtiva é wrapper (pode ser onCameraChange do pai) — intencionalmente fora das deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  // Espelha o modo câmera pro focarScanBar (module-level) — enquanto a câmera manda, o refoco
  // do input fica suspenso pra não popar o teclado sobre o preview.
  useEffect(() => {
    cameraModeAtivo = cameraAtiva;
    return () => { cameraModeAtivo = false; };
  }, [cameraAtiva]);

  // Foco automático — e refoco a cada leitura, sem clique. setTimeout(0) deixa o React
  // terminar o re-render (limpar o input) antes de recolocar o cursor; sem isso um leitor
  // rápido consegue "vencer" o foco perdido entre um scan e o próximo.
  useEffect(() => {
    if (!disabled && !cameraAtiva) inputRef.current?.focus();
  }, [disabled, cameraAtiva]);

  const refocus = () => setTimeout(() => inputRef.current?.focus(), 0);

  /**
   * Casa o EAN com um item da nota e dispara o bipe. Devolve se ACHOU (pra o feedback da
   * câmera) e a promise do `onBipar` (o modo câmera aguarda pra serializar através do diálogo
   * de excedente). O caminho do input ignora o retorno.
   */
  const processarScan = (
    ean: string,
    multiplicador = 1,
  ): { achou: boolean; pronto: Promise<boolean> } => {
    const destino = porEan.get(ean);
    const item = destino && itens.find((i) => i.id === destino.itemId);

    if (!destino || !item) {
      // EAN não pertence a nenhuma embalagem desta nota. Enriquece com o cadastro interno
      // pra dizer AO OPERADOR o que ele acabou de bipar.
      produtoPorBarras.mutate(ean, {
        onSuccess: (resp) => {
          toast.aviso(
            `Produto não esperado nesta nota: ${resp.data.descricao ?? 'sem descrição'} (cód. ${resp.data.codigo}).`
          );
        },
        onError: () => {
          toast.aviso(`Código "${ean}" não corresponde a nenhum item desta nota.`);
        },
      });
      return { achou: false, pronto: Promise.resolve(false) };
    }

    // O multiplicador incide sobre a embalagem lida (destino.unidades já é a QTDEEMBALAGEM):
    // bipar a caixa de 6 com ×12 soma 72. O feedback do que foi contado é do workspace
    // (onBipar → toast pós-persistência); aqui só disparamos e propagamos o desfecho.
    const total = destino.unidades * multiplicador;
    return { achou: true, pronto: Promise.resolve(onBipar(item, total, destino.unidade)) };
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const bruto = codigo.trim();
    setCodigo('');
    if (!disabled && bruto) {
      // Com o multiplicador ligado, o prefixo "N*" vira quantidade; senão a entrada é código cru.
      const { multiplicador, codigo: ean } = multiplicadorAtivo
        ? parseEntradaBipagem(bruto)
        : { multiplicador: 1, codigo: bruto };
      // O toast do que foi contado (com o total já multiplicado) é do workspace, pós-persistência.
      processarScan(ean, multiplicador);
    }
    refocus();
  };

  const onCodigoCamera = async (ean: string) => {
    if (disabled) return;
    if (bipandoRef.current) return;                                    // já processando outro bipe
    if (!controleLeitura.current.deveAceitar(ean, Date.now())) return; // repetição do mesmo código

    bipandoRef.current = true;
    const mult = multiplicadorAtivo ? multCamera : 1;
    try {
      const { achou, pronto } = processarScan(ean, mult);
      (achou ? feedbackSucesso : feedbackErro)();
      // Aguarda o desfecho (segura o próximo bipe até o excedente resolver). `gravou=false`
      // quando o operador cancela o excedente — aí NÃO queima o multiplicador one-shot.
      const gravou = await pronto;
      if (achou && gravou) setMultCamera(1); // one-shot: só zera se a leitura de fato contou
    } finally {
      // Re-carimba a janela a partir do FIM do processamento. Sem isto, se o diálogo de
      // excedente ficasse aberto mais que a janela (o operador lê/decide), cancelar dispararia
      // a MESMA leitura de novo (a caixa segue no enquadramento, o cooldown já expirou) e o
      // diálogo reabriria em loop. Contando do fim, o operador tem ~1,5s pra afastar o volume.
      controleLeitura.current.deveAceitar(ean, Date.now());
      bipandoRef.current = false;
    }
  };

  return (
    <div className="space-y-2.5">
      {cameraAtiva && !disabled && !isMobile && multiplicadorAtivo && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-tx-3">Multiplicar leitura</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setMultCamera((n) => Math.max(1, n - 1))}
              aria-label="Diminuir multiplicador"
              className="flex h-9 w-9 items-center justify-center rounded-iris-sm border border-line-strong bg-surface text-tx-2 hover:bg-surface-2"
            >
              −
            </button>
            <span className="min-w-[3rem] text-center text-base font-bold tabular-nums text-tx">×{multCamera}</span>
            <button
              type="button"
              onClick={() => setMultCamera((n) => Math.min(999, n + 1))}
              aria-label="Aumentar multiplicador"
              className="flex h-9 w-9 items-center justify-center rounded-iris-sm border border-line-strong bg-surface text-tx-2 hover:bg-surface-2"
            >
              +
            </button>
          </div>
          <div className="flex gap-1">
            {[6, 12, 24].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setMultCamera(n)}
                aria-pressed={multCamera === n}
                className={`h-9 min-w-[2.5rem] rounded-iris-sm border px-2 text-sm font-semibold tabular-nums transition-colors ${
                  multCamera === n
                    ? 'border-accent bg-accent text-white'
                    : 'border-line-strong bg-surface text-tx-2 hover:bg-surface-2'
                }`}
              >
                ×{n}
              </button>
            ))}
          </div>
        </div>
      )}

      {cameraAtiva && !disabled && <CameraScanner onCodigo={onCodigoCamera} compact={isMobile} />}

      <form onSubmit={onSubmit} className="flex items-center gap-2">
        <div className="relative flex-1">
          <QrCodeIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-tx-3" />
          <input
            ref={inputRef}
            id={SCAN_INPUT_ID}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder={disabled ? 'Conferência concluída — bipagem desativada' : 'Bipe o código de barras…'}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            disabled={disabled}
            aria-label="Código de barras"
            className={`${inputIris} h-12 w-full pl-10 text-base font-semibold tabular-nums sm:h-11`}
          />
        </div>

        {!disabled && (
          <button
            type="button"
            onClick={() => {
              // prepararAudio DENTRO do gesto (o clique): destrava o WebAudio no iOS pro bip
              // funcionar já na 1ª leitura — só quando está LIGANDO a câmera.
              if (!cameraAtiva) prepararAudio();
              setCameraAtiva(!cameraAtiva);
            }}
            aria-pressed={cameraAtiva}
            aria-label={cameraAtiva ? 'Desligar a câmera' : 'Bipar pela câmera'}
            title={cameraAtiva ? 'Desligar a câmera' : 'Bipar pela câmera'}
            className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-iris-sm border transition-colors sm:h-11 sm:w-11 ${
              cameraAtiva
                ? 'border-accent bg-accent text-white'
                : 'border-line-strong bg-surface text-tx-2 hover:bg-surface-2'
            }`}
          >
            <CameraIcon className="h-5 w-5" />
          </button>
        )}
      </form>
    </div>
  );
};

export default ScanBar;
